import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState, useRef } from "react";
import { hostname, parseError } from "../../../utils";
import {
  ACTIVITY_ACTION_LABELS,
  ACTIVITY_ASSET_TYPE_LABELS,
  ACTIVITY_ACTION_LOGIN,
  ACTIVITY_ACTION_LOGIN_FAILED,
  ACTIVITY_ACTION_MFA_ENABLED,
  ACTIVITY_ACTION_MFA_DISABLED,
  ACTIVITY_ACTION_MFA_VERIFY_FAILED,
  ACTIVITY_ACTION_CREATE,
  ACTIVITY_ACTION_UPDATE,
  ACTIVITY_ACTION_DELETE,
} from "../../../constants";

interface AuditLogEntry {
  id: number;
  actionType: string;
  assetType: string;
  assetId: string | null;
  uid: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdDate: string;
  extra: Record<string, any> | null;
  userDisplayName: string | null;
  userPhoto: string | null;
}

interface AuditLogDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  getToken: () => Promise<string>;
  eventListener: (event: any) => void;
}

const ITEMS_PER_PAGE = 25;

const AuditLogDialog: React.FC<AuditLogDialogProps> = ({
  open,
  onClose,
  companyId,
  getToken,
  eventListener,
}) => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filterAssetType, setFilterAssetType] = useState<string>("");
  const [filterActionType, setFilterActionType] = useState<string>("");

  // Track if we've already fetched for current state to prevent duplicate requests
  const fetchedRef = useRef<string>("");


  const fetchAuditLog = async (
    currentPage: number,
    assetType: string,
    actionType: string
  ) => {
    if (!companyId) return;

    // Create a key to track what we're fetching
    const fetchKey = `${companyId}-${currentPage}-${assetType}-${actionType}`;
    if (fetchedRef.current === fetchKey && logs.length > 0) return;
    fetchedRef.current = fetchKey;

    setLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams({
        companyId,
        page: currentPage.toString(),
        limit: ITEMS_PER_PAGE.toString(),
      });

      if (assetType) {
        params.append("assetType", assetType);
      }
      if (actionType) {
        params.append("actionType", actionType);
      }

      const response = await axios.get(
        `${hostname}/activity-log?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setLogs(response.data.items);
      setTotal(response.data.pagination.total);
      setTotalPages(response.data.pagination.totalPages);
    } catch (err: any) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch when dialog opens or filters/page change
  useEffect(() => {
    if (open) {
      fetchAuditLog(page, filterAssetType, filterActionType);
    } else {
      // Reset state when dialog closes
      fetchedRef.current = "";
    }
  }, [open, page, filterAssetType, filterActionType, companyId]);

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleRefresh = () => {
    fetchedRef.current = ""; // Clear the fetch cache to force refresh
    fetchAuditLog(page, filterAssetType, filterActionType);
  };

  const getActionChipColor = (actionType: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (actionType) {
      case ACTIVITY_ACTION_LOGIN:
        return "primary";
      case ACTIVITY_ACTION_LOGIN_FAILED:
      case ACTIVITY_ACTION_MFA_VERIFY_FAILED:
        return "error";
      case ACTIVITY_ACTION_MFA_ENABLED:
        return "primary";
      case ACTIVITY_ACTION_MFA_DISABLED:
        return "warning";
      case ACTIVITY_ACTION_CREATE:
        return "primary";
      case ACTIVITY_ACTION_UPDATE:
        return "info";
      case ACTIVITY_ACTION_DELETE:
        return "error";
      default:
        return "default";
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getExtraInfo = (entry: AuditLogEntry): string => {
    if (!entry.extra) return "";
    return entry.extra.detail || "";
  };

  // Asset type options for filter
  const assetTypeOptions = Object.entries(ACTIVITY_ASSET_TYPE_LABELS);

  // Action type options for filter
  const actionTypeOptions = Object.entries(ACTIVITY_ACTION_LABELS);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: "85vh",
        },
      }}
    >
      <DialogTitle>
        Audit Log
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Filters */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mt: 1,
            mb: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <FilterListIcon color="action" />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Asset Type</InputLabel>
            <Select
              value={filterAssetType}
              label="Asset Type"
              onChange={(e) => {
                setFilterAssetType(e.target.value);
                setPage(1); // Reset to first page when filter changes
              }}
            >
              <MenuItem value="">All</MenuItem>
              {assetTypeOptions.map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Action Type</InputLabel>
            <Select
              value={filterActionType}
              label="Action Type"
              onChange={(e) => {
                setFilterActionType(e.target.value);
                setPage(1); // Reset to first page when filter changes
              }}
            >
              <MenuItem value="">All</MenuItem>
              {actionTypeOptions.map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {total} total entries
          </Typography>
        </Box>

        {/* Table */}
        <TableContainer component={Paper} variant="outlined">
          {loading && <LinearProgress />}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Asset Type</TableCell>
                <TableCell>Details</TableCell>
                <TableCell>User</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No audit logs found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {formatDate(log.createdDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ACTIVITY_ACTION_LABELS[log.actionType] || log.actionType}
                        color={getActionChipColor(log.actionType)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {ACTIVITY_ASSET_TYPE_LABELS[log.assetType] || log.assetType}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ maxWidth: 300 }}
                        noWrap
                      >
                        {getExtraInfo(log) || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar
                          src={log.userPhoto || undefined}
                          sx={{ width: 24, height: 24 }}
                        >
                          {log.userDisplayName?.charAt(0)?.toUpperCase() || "?"}
                        </Avatar>
                        <Typography variant="body2" noWrap>
                          {log.userDisplayName || "-"}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuditLogDialog;
